import { strFromU8, unzipSync } from 'fflate';
import { parseSlicerText } from './ocr.js';

const MODEL_EXTENSIONS = /\.(stl|3mf)$/i;
const MAX_MODEL_FILE_BYTES = 100 * 1024 * 1024;
const MAX_ARCHIVE_ENTRY_BYTES = 80 * 1024 * 1024;
const MATERIAL_DENSITY_G_CM3 = {
  PLA: 1.24,
  PETG: 1.27,
  ABS: 1.04,
  ASA: 1.07,
  TPU: 1.21,
  PA: 1.15,
  NYLON: 1.15,
  PC: 1.2,
  PVA: 1.23,
  HIPS: 1.04,
  RESIN: 1.1,
};

export function isModelPrintFile(file) {
  return Boolean(file && MODEL_EXTENSIONS.test(file.name || ''));
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function materialDensity(materialType) {
  const normalized = String(materialType || '').trim().toUpperCase();
  const direct = MATERIAL_DENSITY_G_CM3[normalized];
  if (direct) return direct;
  const partial = Object.entries(MATERIAL_DENSITY_G_CM3).find(([key]) => normalized.includes(key));
  return partial?.[1] || MATERIAL_DENSITY_G_CM3.PLA;
}

function emptyMeshStats() {
  return {
    signedVolumeMm3: 0,
    surfaceAreaMm2: 0,
    triangleCount: 0,
    objectCount: 0,
    min: [Infinity, Infinity, Infinity],
    max: [-Infinity, -Infinity, -Infinity],
  };
}

function addVertexBounds(stats, vertex) {
  for (let axis = 0; axis < 3; axis += 1) {
    stats.min[axis] = Math.min(stats.min[axis], vertex[axis]);
    stats.max[axis] = Math.max(stats.max[axis], vertex[axis]);
  }
}

function addTriangle(stats, a, b, c) {
  if (![a, b, c].every((vertex) => vertex?.every(Number.isFinite))) return;
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const cross = [
    (ab[1] * ac[2]) - (ab[2] * ac[1]),
    (ab[2] * ac[0]) - (ab[0] * ac[2]),
    (ab[0] * ac[1]) - (ab[1] * ac[0]),
  ];
  stats.surfaceAreaMm2 += Math.hypot(...cross) / 2;
  stats.signedVolumeMm3 += (
    (a[0] * ((b[1] * c[2]) - (b[2] * c[1])))
    - (a[1] * ((b[0] * c[2]) - (b[2] * c[0])))
    + (a[2] * ((b[0] * c[1]) - (b[1] * c[0])))
  ) / 6;
  stats.triangleCount += 1;
  addVertexBounds(stats, a);
  addVertexBounds(stats, b);
  addVertexBounds(stats, c);
}

function finishMeshStats(stats) {
  const hasBounds = stats.min.every(Number.isFinite) && stats.max.every(Number.isFinite);
  return {
    volumeMm3: Math.abs(stats.signedVolumeMm3),
    surfaceAreaMm2: stats.surfaceAreaMm2,
    triangleCount: stats.triangleCount,
    objectCount: stats.objectCount || (stats.triangleCount ? 1 : 0),
    dimensionsMm: hasBounds ? stats.max.map((value, index) => Math.max(0, value - stats.min[index])) : [0, 0, 0],
  };
}

function mergeFinishedMeshStats(items) {
  return items.reduce((summary, item) => ({
    volumeMm3: summary.volumeMm3 + numberValue(item.volumeMm3),
    surfaceAreaMm2: summary.surfaceAreaMm2 + numberValue(item.surfaceAreaMm2),
    triangleCount: summary.triangleCount + numberValue(item.triangleCount),
    objectCount: summary.objectCount + numberValue(item.objectCount),
    dimensionsMm: summary.dimensionsMm.map((value, index) => Math.max(value, numberValue(item.dimensionsMm?.[index]))),
  }), { volumeMm3: 0, surfaceAreaMm2: 0, triangleCount: 0, objectCount: 0, dimensionsMm: [0, 0, 0] });
}

function parseBinaryStl(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < 84) return null;
  const triangleCount = view.getUint32(80, true);
  if (!triangleCount || 84 + (triangleCount * 50) > view.byteLength) return null;
  const stats = emptyMeshStats();
  stats.objectCount = 1;
  for (let index = 0; index < triangleCount; index += 1) {
    const offset = 84 + (index * 50) + 12;
    const vertices = [0, 1, 2].map((vertexIndex) => {
      const vertexOffset = offset + (vertexIndex * 12);
      return [
        view.getFloat32(vertexOffset, true),
        view.getFloat32(vertexOffset + 4, true),
        view.getFloat32(vertexOffset + 8, true),
      ];
    });
    addTriangle(stats, vertices[0], vertices[1], vertices[2]);
  }
  return finishMeshStats(stats);
}

function parseAsciiStl(arrayBuffer) {
  const text = new TextDecoder().decode(arrayBuffer);
  const vertices = [...text.matchAll(/\bvertex\s+([-+\d.eE]+)\s+([-+\d.eE]+)\s+([-+\d.eE]+)/gi)]
    .map((match) => [Number(match[1]), Number(match[2]), Number(match[3])]);
  if (vertices.length < 3) return null;
  const stats = emptyMeshStats();
  stats.objectCount = Math.max(1, [...text.matchAll(/\bsolid\b/gi)].length);
  for (let index = 0; index + 2 < vertices.length; index += 3) addTriangle(stats, vertices[index], vertices[index + 1], vertices[index + 2]);
  return finishMeshStats(stats);
}

export function analyzeStlBuffer(arrayBuffer) {
  const binary = parseBinaryStl(arrayBuffer);
  const stats = binary?.triangleCount ? binary : parseAsciiStl(arrayBuffer);
  if (!stats?.triangleCount) throw new Error('MM could not find a valid triangle mesh in this STL file.');
  return stats;
}

function xmlAttribute(tag, key) {
  const match = String(tag || '').match(new RegExp(`\\b${key}\\s*=\\s*["']([^"']+)["']`, 'i'));
  return match?.[1] || '';
}

function unitScaleFromModel(text) {
  const modelTag = String(text || '').match(/<model\b[^>]*>/i)?.[0] || '';
  const unit = xmlAttribute(modelTag, 'unit').toLowerCase() || 'millimeter';
  return ({ micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4, foot: 304.8, meter: 1000 })[unit] || 1;
}

export function analyze3mfModelText(text) {
  const scale = unitScaleFromModel(text);
  const meshes = [...String(text || '').matchAll(/<mesh\b[^>]*>([\s\S]*?)<\/mesh>/gi)];
  const finishedMeshes = meshes.map((meshMatch) => {
    const mesh = meshMatch[1];
    const vertices = [...mesh.matchAll(/<vertex\b[^>]*>/gi)].map((match) => {
      const tag = match[0];
      return ['x', 'y', 'z'].map((axis) => numberValue(xmlAttribute(tag, axis)) * scale);
    });
    const triangles = [...mesh.matchAll(/<triangle\b[^>]*>/gi)];
    const stats = emptyMeshStats();
    stats.objectCount = triangles.length ? 1 : 0;
    triangles.forEach((match) => {
      const tag = match[0];
      addTriangle(stats, vertices[Number(xmlAttribute(tag, 'v1'))], vertices[Number(xmlAttribute(tag, 'v2'))], vertices[Number(xmlAttribute(tag, 'v3'))]);
    });
    return finishMeshStats(stats);
  }).filter((stats) => stats.triangleCount);
  return mergeFinishedMeshStats(finishedMeshes);
}

function numbersForAttribute(text, key) {
  const values = [];
  const attributePattern = new RegExp(`\\b${key}\\s*=\\s*["']([-+\\d.eE]+)["']`, 'gi');
  const jsonPattern = new RegExp(`["']${key}["']\\s*:\\s*([-+\\d.eE]+)`, 'gi');
  for (const pattern of [attributePattern, jsonPattern]) {
    for (const match of String(text || '').matchAll(pattern)) {
      const value = Number(match[1]);
      if (Number.isFinite(value)) values.push(value);
    }
  }
  return values;
}

function metadataValues(text, key) {
  return [...String(text || '').matchAll(/<metadata\b[^>]*>/gi)]
    .filter((match) => xmlAttribute(match[0], 'key').toLowerCase() === key.toLowerCase())
    .map((match) => Number(xmlAttribute(match[0], 'value')))
    .filter(Number.isFinite);
}

function objectCountFromSliceInfo(text) {
  return [...String(text || '').matchAll(/<object\b[^>]*>/gi)]
    .filter((match) => !/\bskipped\s*=\s*["'](?:true|1)["']/i.test(match[0])).length;
}

function geometryEstimate(geometry, materialType) {
  const solidVolume = numberValue(geometry?.volumeMm3);
  if (solidVolume <= 0) return null;
  const surfaceShellVolume = Math.min(solidVolume, numberValue(geometry.surfaceAreaMm2) * 0.8);
  const assumedExtrusionVolume = Math.min(solidVolume, surfaceShellVolume + ((solidVolume - surfaceShellVolume) * 0.2)) * 1.08;
  const grams = (assumedExtrusionVolume / 1000) * materialDensity(materialType);
  const minutes = Math.max(1, Math.round((assumedExtrusionVolume / (4 * 60)) + 5));
  return {
    fields: {
      filamentGrams: Math.round(grams * 10) / 10,
      printHours: Math.floor(minutes / 60),
      printMinutes: minutes % 60,
      quantity: Math.max(1, numberValue(geometry.objectCount, 1)),
    },
    fieldConfidence: { filamentGrams: 36, printTime: 28, quantity: 30 },
    notes: 'Geometry estimate assumes 0.8 mm shell coverage, 20% internal infill, 8% extra material, and 4 mmÂ³/s effective extrusion. Supports, brims, purge, printer speed limits, and slicer overrides are unknown.',
  };
}

function durationFields(totalMinutes) {
  const rounded = Math.max(1, Math.round(totalMinutes));
  return { printHours: Math.floor(rounded / 60), printMinutes: rounded % 60 };
}

export function analyze3mfTextEntries(entries = [], materialType = '', sourceName = 'uploaded.3mf') {
  const metadataEntries = entries.filter((entry) => !/\.model$/i.test(entry.name));
  const modelEntries = entries.filter((entry) => /\.model$/i.test(entry.name));
  const sliceInfo = metadataEntries.filter((entry) => /slice_info\.config$/i.test(entry.name));
  const searchableText = metadataEntries.map((entry) => entry.text).join('\n');
  const parsed = parseSlicerText(searchableText, 100, sourceName);
  const exactFields = { ...parsed.fields };
  const exactConfidence = { ...parsed.fieldConfidence };

  const usedGrams = sliceInfo.flatMap((entry) => numbersForAttribute(entry.text, 'used_g')).filter((value) => value > 0 && value < 100000);
  const metadataWeights = sliceInfo.flatMap((entry) => metadataValues(entry.text, 'weight')).filter((value) => value > 0 && value < 100000);
  const filamentGrams = usedGrams.length ? usedGrams.reduce((sum, value) => sum + value, 0) : metadataWeights.reduce((sum, value) => sum + value, 0);
  if (filamentGrams > 0) {
    exactFields.filamentGrams = Math.round(filamentGrams * 10) / 10;
    exactConfidence.filamentGrams = 96;
  }

  if (exactFields.printHours === undefined) {
    const predictionSeconds = sliceInfo.flatMap((entry) => metadataValues(entry.text, 'prediction')).filter((value) => value > 0 && value < 60 * 60 * 24 * 60);
    if (predictionSeconds.length) {
      Object.assign(exactFields, durationFields(predictionSeconds.reduce((sum, value) => sum + value, 0) / 60));
      exactConfidence.printTime = 94;
    }
  }

  if (exactFields.quantity === undefined) {
    const objectCount = sliceInfo.reduce((sum, entry) => sum + objectCountFromSliceInfo(entry.text), 0);
    if (objectCount) {
      exactFields.quantity = objectCount;
      exactConfidence.quantity = 82;
    }
  }

  const geometry = mergeFinishedMeshStats(modelEntries.map((entry) => analyze3mfModelText(entry.text)).filter((stats) => stats.triangleCount));
  const estimated = geometryEstimate(geometry, parsed.detectedMaterial || materialType);
  const hasExactFilament = exactFields.filamentGrams !== undefined;
  const hasExactTime = exactFields.printHours !== undefined;
  const fields = { ...(estimated?.fields || {}), ...exactFields };
  const fieldConfidence = { ...(estimated?.fieldConfidence || {}), ...exactConfidence };
  const confidenceValues = Object.values(fieldConfidence);

  if (!Object.keys(fields).length) throw new Error('This 3MF does not include slicer totals or a readable mesh. Export a sliced 3MF or enter the slicer totals manually.');

  return {
    fields,
    fieldConfidence,
    extractionConfidence: confidenceValues.length ? Math.round(confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length) : 0,
    method: hasExactFilament && hasExactTime ? 'slicer-metadata' : 'geometry-estimate',
    exactFields: [hasExactFilament ? 'filamentGrams' : '', hasExactTime ? 'printTime' : ''].filter(Boolean),
    detectedMaterial: parsed.detectedMaterial || materialType || '',
    detectedSlicer: parsed.detectedSlicer,
    rawText: searchableText.trim().slice(0, 12000),
    geometry,
    notes: hasExactFilament && hasExactTime
      ? 'Filament and print time came from slicer metadata or embedded G-code in the 3MF archive.'
      : estimated?.notes || 'Only part of the slicer summary was available. Review every imported value before pricing.',
  };
}

function analysisFromGeometry(geometry, materialType, detectedSlicer = 'Unknown slicer') {
  const estimated = geometryEstimate(geometry, materialType);
  if (!estimated) throw new Error('MM could not calculate a closed model volume from this file. Repair or slice the model, then upload a sliced 3MF or screenshot.');
  const confidenceValues = Object.values(estimated.fieldConfidence);
  return {
    ...estimated,
    extractionConfidence: Math.round(confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length),
    method: 'geometry-estimate',
    exactFields: [],
    detectedMaterial: materialType || '',
    detectedSlicer,
    rawText: '',
    geometry,
  };
}

function relevantArchiveEntry(info) {
  return /\.(?:gcode|config|json|xml|model|ini|txt)$/i.test(info.name || '')
    && numberValue(info.originalSize) <= MAX_ARCHIVE_ENTRY_BYTES;
}

function archiveText(name, bytes) {
  if (/\.gcode$/i.test(name) && bytes.length > 3 * 1024 * 1024) {
    const first = strFromU8(bytes.subarray(0, 1024 * 1024));
    const last = strFromU8(bytes.subarray(bytes.length - (2 * 1024 * 1024)));
    return `${first}\n${last}`;
  }
  return strFromU8(bytes);
}

export async function analyzeModelFile(file, { materialType = '' } = {}) {
  if (!isModelPrintFile(file)) throw new Error('Model analysis supports STL and 3MF files.');
  if (numberValue(file.size) > MAX_MODEL_FILE_BYTES) throw new Error('This model file is larger than the 100 MB analysis limit.');
  const arrayBuffer = await file.arrayBuffer();
  if (/\.stl$/i.test(file.name || '')) return analysisFromGeometry(analyzeStlBuffer(arrayBuffer), materialType);

  let archive;
  try {
    archive = unzipSync(new Uint8Array(arrayBuffer), { filter: relevantArchiveEntry });
  } catch {
    throw new Error('MM could not open this 3MF archive. Confirm the file is complete and not password protected.');
  }
  const entries = Object.entries(archive).map(([name, bytes]) => ({ name, text: archiveText(name, bytes) }));
  return analyze3mfTextEntries(entries, materialType, file.name);
}
